import React, { useState, useEffect } from 'react';
import '../styles/StoreInfoManagement.css';
import { useNavigate } from 'react-router-dom';
import { getCompanyInfo, updateCompanyInfo } from '../utils/api';
import Dialog from '../components/Dialog';

const StoreInfoManagement = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [storeInfo, setStoreInfo] = useState({
    companyName: '',
    address: '',
    businessNumber: '',
    name: '',
    phone: ''
  });
  
  // 주소 좌표 관련 상태
  const [coordinates, setCoordinates] = useState(null);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressError, setAddressError] = useState(false);
  
  // 다이얼로그 관련 상태
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  
  const navigate = useNavigate();

  // 주소 API 호출 함수
  const fetchAddressCoordinates = async (address) => {
    if (!address || address.trim() === '') return;
    
    try {
      setAddressLoading(true);
      setAddressError(false);
      const BASE_URL = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${BASE_URL}/api/geocode/public?address=${encodeURIComponent(address)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('주소 좌표를 가져오는데 실패했습니다.');
      }

      const data = await response.json();
      console.log('주소 좌표 응답:', data);
      
      if (data.status === 'success' && data.x && data.y) {
        setCoordinates(data);
        setAddressError(false);
      } else {
        setCoordinates(null);
        setAddressError(true);
      }
    } catch (error) {
      console.error('주소 좌표 가져오기 오류:', error);
      setCoordinates(null);
      setAddressError(true);
    } finally {
      setAddressLoading(false);
    }
  };

  // 회사 정보 가져오기
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        setLoading(true);
        
        // 로컬 스토리지에서 사용자 정보 가져오기
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        const companyCode = userInfo.middleCompanyCode || userInfo.businessNumber;
        
        if (!companyCode) {
          throw new Error('회사 코드를 찾을 수 없습니다.');
        }
        
        const response = await getCompanyInfo(companyCode);
        
        if (response.status === 'success' && response.data) {
          // API 응답 데이터 형식에 맞게 매핑
          setStoreInfo({
            companyName: response.data.company_name || '',
            address: response.data.address || '',
            businessNumber: response.data.business_number || '',
            name: response.data.ceo_name || '',
            phone: response.data.phone_number || ''
          });
        }
      } catch (err) {
        console.error('회사 정보 로드 오류:', err);
        setError(err.message || '회사 정보를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyInfo();
  }, []);
  
  // 주소 입력 후 일정 시간이 지나면 API 호출
  useEffect(() => {
    if (!isEditing) return; // 수정 모드일 때만 실행
    
    const timer = setTimeout(() => {
      if (storeInfo.address.trim().length > 5) {
        fetchAddressCoordinates(storeInfo.address);
      }
    }, 1000); // 1초 딜레이

    return () => clearTimeout(timer);
  }, [storeInfo.address, isEditing]);

  // 수정 모드 토글 및 저장 처리
  const toggleEditMode = async () => {
    if (isEditing) {
      // 주소 좌표 검증
      if (addressError) {
        setDialogMessage('유효한 지점 주소를 입력해주세요.');
        setDialogType(Dialog.TYPES.ERROR);
        setDialogOpen(true);
        return;
      }
      
      // 수정 완료 시 API 호출
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        const companyCode = userInfo.middleCompanyCode || userInfo.businessNumber;
        
        const updateData = {
          company_name: storeInfo.companyName,
          address: storeInfo.address,
          business_number: storeInfo.businessNumber,
          ceo_name: storeInfo.name,
          phone_number: storeInfo.phone
        };
        
        // 좌표값이 있는 경우 추가
        if (coordinates && coordinates.x && coordinates.y) {
          updateData.lat = coordinates.y;  // y 좌표는 위도(lat)
          updateData.lng = coordinates.x; // x 좌표는 경도(lng)
        }
        
        await updateCompanyInfo(companyCode, updateData);
        // 성공 다이얼로그 표시
        setDialogType(Dialog.TYPES.COMPANY_EDIT_SUCCESS);
        setDialogOpen(true);
        
        // 수정 모드 종료
        setIsEditing(false);
        
        // 좌표 정보 초기화
        setCoordinates(null);
        setAddressError(false);
      } catch (err) {
        console.error('회사 정보 업데이트 오류:', err);
        setDialogMessage(err.message || '회사 정보 업데이트에 실패했습니다.');
        setDialogType(Dialog.TYPES.COMPANY_EDIT_ERROR);
        setDialogOpen(true);
        return; // 실패 시 수정 모드 유지
      }
    } else {
      // 수정 모드 진입
      setIsEditing(true);
    }
  };

  // 다이얼로그 닫기 핸들러
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // 다이얼로그 확인 핸들러
  const handleConfirmDialog = () => {
    setDialogOpen(false);
  };

  // 입력 필드 변경 핸들러
  const handleInputChange = (field, value) => {
    setStoreInfo({
      ...storeInfo,
      [field]: value
    });
    
    // 주소 변경 시 좌표값 초기화
    if (field === 'address') {
      setCoordinates(null);
      setAddressError(false);
    }
  };

  if (loading) return <div className="loading">정보를 불러오는 중...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="storeInfoContainer">
      <div className="storeInfoHeader">
        <h1 className="pageTitle">지점 정보 관리</h1>
        <button 
          className={`edit-button ${isEditing ? 'save-mode' : ''}`} 
          onClick={toggleEditMode}
        >
          {isEditing ? (
            <>
              <span className="ok-icon">OK</span>
              수정완료
            </>
          ) : (
            <>
              <span className="edit-icon">✎</span>
              수정하기
            </>
          )}
        </button>
      </div>

      <div className="storeInfoContent">
        <div className="infoField">
          <div className="fieldLabel">업체명</div>
          {isEditing ? (
            <input
              type="text"
              className="fieldInput"
              value={storeInfo.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
            />
          ) : (
            <div className="fieldValue">{storeInfo.companyName}</div>
          )}
        </div>

        <div className="infoField">
          <div className="fieldLabel">지점 주소</div>
          <div className="exampleText">예시: 서울특별시 송파구 올림픽로 300</div>
          {isEditing ? (
            <div className="addressInputContainer">
            <input
              type="text"
              className="fieldInput"
              value={storeInfo.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
            />
              {addressLoading && <div className="address-loading">주소 검색 중...</div>}
              {addressError && (
                <div className="address-error">유효하지 않는 위치입니다</div>
              )}
              {coordinates && coordinates.x && coordinates.y && (
                <div className="coordinates-result">
                  <p>x: {coordinates.x}, y: {coordinates.y}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="fieldValue">{storeInfo.address}</div>
          )}
        </div>

        <div className="infoField">
          <div className="fieldLabel">사업자번호</div>
          {isEditing ? (
            <input
              type="text"
              className="fieldInput"
              value={storeInfo.businessNumber}
              onChange={(e) => handleInputChange('businessNumber', e.target.value)}
            />
          ) : (
            <div className="fieldValue">{storeInfo.businessNumber}</div>
          )}
        </div>

        <div className="infoField">
          <div className="fieldLabel">이름</div>
          {isEditing ? (
            <input
              type="text"
              className="fieldInput"
              value={storeInfo.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
            />
          ) : (
            <div className="fieldValue">{storeInfo.name}</div>
          )}
        </div>

        <div className="infoField">
          <div className="fieldLabel">전화번호</div>
          {isEditing ? (
            <input
              type="text"
              className="fieldInput"
              value={storeInfo.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
            />
          ) : (
            <div className="fieldValue">{storeInfo.phone}</div>
          )}
        </div>
      </div>

      {/* 다이얼로그 컴포넌트 */}
      <Dialog
        isOpen={dialogOpen}
        type={dialogType}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmDialog}
        message={dialogMessage}
      />
    </div>
  );
};

export default StoreInfoManagement; 